// External Libs
import React from "react"

// External files
import { customColor } from "../../helpers/colorHelper"
import { fetchCampaign } from "../../services/dataStore"
import { SIGNUP_URL } from "../../constants/urls"
import {
  PROMO_CODE_COOKIE,
  COOKIE_DOMAIN,
  COOKIE_PATH,
} from "../../constants/cookies"

// Non Typed External Libs
const Cookies = require("js-cookie")
const Modal = require("react-modal")

/* eslint-disable camelcase */

interface ReferralCampaign {
  bannerTitle: string,
  bannerShortTitle: string | null,
  bannerCta: string,
  code: string,
  finePrint?: string,
}

interface BannerBarProps {
  backgroundColor?: string,
  textColor?: string,
}

interface BannerBarState extends ReferralCampaign {
  hidden: boolean,
  modalOpen: boolean,
}

class BannerBarInner extends React.Component<BannerBarProps, BannerBarState> {
  constructor(props = {}) {
    super(props)

    this.state = {
      bannerTitle: "",
      bannerShortTitle: null,
      bannerCta: "",
      code: "",
      finePrint: "",
      hidden: true,
      modalOpen: false,
    }

    this.clickedCTA = this.clickedCTA.bind(this)
    this.getPromoCode = this.getPromoCode.bind(this)
    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  componentDidMount() {
    const code = this.getPromoCode()
    this.fetchData(code)
    Modal.setAppElement("body")
  }

  private getPromoCode(): string {
    const code: string = Cookies.get(PROMO_CODE_COOKIE, { domain: COOKIE_DOMAIN })
    return code
  }

  private savePromoCode(code: string) {
    Cookies.set(
      PROMO_CODE_COOKIE,
      code,
      { domain: COOKIE_DOMAIN, path: COOKIE_PATH },
    )
  }

  private redirectInvalidCampaign() {
    const cookieCode = this.getPromoCode()
    if (cookieCode) {
      Cookies.remove(PROMO_CODE_COOKIE, { domain: COOKIE_DOMAIN, path: COOKIE_PATH })
      this.redirectHome()
    }
  }

  private redirectHome() {
    if (window.location.pathname !== "/") {
      window.location.replace("/")
    }
  }

  private async fetchData(code) {
    try {
      const query = "bannerTitle bannerShortTitle bannerCta code finePrint"
      const campaign = await fetchCampaign(query, code)
      this.initializeComponent(campaign)
    } catch (error) {
      this.redirectInvalidCampaign()
    }
  }

  private initializeComponent(campaign: ReferralCampaign) {
    const { code, bannerTitle } = campaign

    if (code && bannerTitle) {
      this.setState({ ...campaign, hidden: false })
    }
  }

  private clickedCTA(event) {
    event.preventDefault()
    const { code } = this.state
    this.savePromoCode(code)
    this.redirectUser(event.target.getAttribute("href"))
  }

  private redirectUser(path: string): void {
    if (path) {
      window.location.href = path
    }
  }

  private openModal() {
    this.setState({ modalOpen: true })
  }

  private closeModal() {
    this.setState({ modalOpen: false })
  }

  private shouldDisplayFinePrintModal() {
    const { finePrint } = this.state
    return finePrint && finePrint.length > 0
  }

  private renderTitle() {
    const { bannerTitle, bannerShortTitle } = this.state

    return (
      <>
        <span className="hidden md:inline-block">
          { bannerTitle }
        </span>
        <span className="md:hidden">
          { bannerShortTitle || bannerTitle }
        </span>
      </>
    )
  }

  private renderBanner() {
    const {
      bannerCta,
      code,
    } = this.state

    const {
      backgroundColor,
      textColor,
    } = this.props

    return (
      <section aria-label="Limited-time promotion">
        <div
          className="bg-white relative p-3 text-center shadow-[0px_4px_4px_0px_rgba(0,0,0,0.05)]"
          style={customColor({ background: backgroundColor })}
        >
          <div className="text-base font-bold text-black-secondary" style={customColor({ text: textColor })}>
            {this.renderTitle()}

            <span className="block mt-2 md:mt-0 md:inline-block">
              <span className="inline-block">
                <a
                  className="inline-block text-center no-underline hover:underline
                    w-full text-sm border border-transparent md:ml-2.5"
                  style={customColor({ background: textColor, text: backgroundColor })}
                  href={`${SIGNUP_URL}?promo_code=${code}`}
                  onClick={this.clickedCTA}
                  role="button"
                  tabIndex={0}
                >
                  {bannerCta}
                </a>
              </span>
              {this.shouldDisplayFinePrintModal() && (
                <span className="ml-4 relative">
                  <span className="bg-black-secondary absolute h-4 w-px left-0.5 top-0 bottom-0 my-auto" />
                  <button
                    className="ml-4 inline-block text-sm font-medium hover:underline"
                    onClick={this.openModal}
                    style={customColor({ text: textColor })}
                    tabIndex={0}
                  >
                    Get Details
                  </button>
                </span>
              )}
            </span>
          </div>

        </div>
        {this.shouldDisplayFinePrintModal() && this.renderModal()}
      </section>
    )
  }

  private renderModal() {
    const { code, modalOpen, finePrint } = this.state
    const modalStyle = {
      overlay: { zIndex: 9999 },
    }
    return (
      <Modal
        isOpen={modalOpen}
        className="modal mise"
        style={modalStyle}
        ariaHideApp={false}
        onRequestClose={this.closeModal}
      >
        <div className="fixed z-10 inset-0 overflow-y-auto backdrop-blur-sm">
          <div className="flex justify-center pt-4 px-4 text-center">
            <div
              className="inline-block align-bottom bg-white rounded-lg text-left
                        overflow-hidden shadow-xl transform transition-all my-8
                        align-middle w-full p-5 md:w-1/2 lg:w-1/3"
            >
              <div>
                <h3 className="text-base leading-6 font-medium text-gray-900">
                  Offer Details
                </h3>
                <div className="mt-2">
                  <p className="text-sm leading-5 text-black-secondary">
                    {finePrint}
                  </p>
                </div>
              </div>
              <div className="mt-5 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <span className="flex w-full sm:col-start-2">
                  <a
                    href={`${SIGNUP_URL}?promo_code=${code}`}
                    className="inline-block text-center font-bold rounded-full leading-snug
                      no-underline transition-all duration-200 ease-in-out
                      active:[box-shadow:0px_4px_4px_0px_rgba(0,0,0,0.25)_inset]
                      hover:no-underline w-full text-base border border-transparent
                      px-6 py-3.5 bg-black text-white focus:bg-black-secondary
                      hover:bg-black-secondary active:bg-gray-dark"
                    onClick={this.clickedCTA}
                    type="button"
                  >
                    Apply Offer
                  </a>
                </span>
                <span className="mt-3 flex w-full sm:mt-0 sm:col-start-1">
                  <button
                    type="button"
                    className="inline-block text-center font-bold rounded-full leading-snug
                      no-underline transition-all duration-200 ease-in-out
                      active:[box-shadow:0px_4px_4px_0px_rgba(0,0,0,0.25)_inset]
                      hover:no-underline w-full text-base border border-black px-6 py-3.5
                      bg-white text-black focus:bg-gray-neutral hover:bg-gray-neutral"
                    onClick={this.closeModal}
                  >
                    Cancel
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  render() {
    const { hidden } = this.state
    if (hidden) { return null }

    return (this.renderBanner())
  }
}

export function BannerBar(props: BannerBarProps) {
  return <BannerBarInner {...props} />
}
